import React from "react";
import classNames from "classnames";
import { useUser } from "../../shared/context/UserContext";
import { themeStyles } from "../services/themes";
import { colorOptions, fontOptions } from "../modules/profile/theme/themeConstants";

interface ButtonProps {
  label: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "secondary" | "danger" | "confirm" | "cancel" | "arrow" | "ghost" | "outline" | "neworder" | "outlineDanger";
  size?: "sm" | "md" | "lg";
  icon?: React.ElementType;
  iconPosition?: "left" | "right";
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  fullWidth?: boolean;
  rounded?: "full" | "lg" | "md" | "none";
}

const CommonButtons: React.FC<ButtonProps> = ({
  label,
  onClick,
  type = "button",
  variant = "primary",
  size = "md",
  icon: Icon,
  iconPosition = "left",
  disabled = false,
  loading = false,
  fullWidth = false,
  rounded = "full",
  className = "",
}) => {
  const { user } = useUser();
    // Theme values
  const themeKey = user?.themes.backgroundColour || "DodgerBlue";
  const themeColor = colorOptions[themeKey];
  const fontColor = user?.themes.fontFamily || "#fff";
  const fontClass = fontOptions[user?.themes.fontFamily || "Sans-serif"];
  const colorSet = themeStyles[themeColor as keyof typeof themeStyles] || themeStyles["Blue"];

  // Extract HEX or Tailwind class for theme border/text color
  const themeHexMatch = themeColor?.match?.(/#([0-9A-Fa-f]{6})/) || null;
  const themeHex = themeHexMatch ? themeHexMatch[0] : undefined;
  const themeBorder = themeHex ? `border-[${themeHex}]` : `border-${themeKey.toLowerCase()}-600`;
  const themeText = themeHex ? `text-[${themeHex}]` : `text-${themeKey.toLowerCase()}-600`;

  const variantStyles: Record<string, string> = {
    primary: `${colorSet} hover:brightness-110 active:brightness-90`,
    secondary: "bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-800",
    danger: "bg-red-600 hover:bg-red-700 active:bg-red-800 text-white",
    confirm: "bg-green-600 hover:bg-green-700 active:bg-green-800 text-white",
    cancel: "bg-red-500 hover:bg-red-600 active:bg-red-700 text-white",
    arrow: "", // special handling below
    ghost: "bg-transparent hover:bg-gray-100 active:bg-gray-200 text-gray-800",
    outline: `bg-transparent border-2 ${colorSet.replace("bg-", "border-")} text-black hover:bg-opacity-10 active:bg-opacity-20`,
    outlineDanger: `bg-transparent border-2 ${themeBorder} ${themeText} hover:bg-opacity-10 active:bg-opacity-20`,
    neworder: `bg-[#e06d0f] active:bg-[#e06d0f] text-white`,
  };

  const sizeStyles: Record<string, string> = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  const roundedStyles: Record<string, string> = {
    full: "rounded-full",
    lg: "rounded-lg",
    md: "rounded-md",
    none: "rounded-none",
  };

  // Determine final style
  const useThemeVariant = !variant || variant === "primary";

  return (
    <button
      type={type}
      id={`${label}-button`}
      onClick={onClick}
      disabled={disabled || loading}
      // style={ variant === "arrow" || useThemeVariant ? { 
      //   backgroundColor: themeColor, color: fontColor,  } : {}
      // }
      className={classNames(
        "flex items-center justify-center gap-2 font-medium transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        !useThemeVariant && variant && variantStyles[variant],
        fontClass,
        useThemeVariant && themeColor,
        fontColor,
        sizeStyles[size],
        roundedStyles[rounded],
        fullWidth && "w-full",
        loading && "cursor-wait",
        className
      )}
    >
      {loading ? (
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
      ) : (
        <>
          {Icon && iconPosition === "left" && <Icon className={size === "sm" ? "h-4 w-4" : "h-5 w-5"} />}
          <span className="font-bold">{label}</span>
          {Icon && iconPosition === "right" && <Icon className={size === "sm" ? "h-4 w-4" : "h-5 w-5"} />}
        </>
      )}
    </button>
  );
};

export default CommonButtons;
